import { AlertTriangle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { ClientsService } from "../services/clientsService";
import type { ClientAccount, CreateClientAccountRequest } from "../types/clients-api.types";
import { PublicAccountPaymentMethod as PublicAccountPaymentMethodEnum } from "../types/clients-api.types";
import {
  canDeleteOrganizationAccounts,
  canDeleteSubclientAccounts,
  canWriteClientAccounts,
} from "../utils/clientsPermissions";
import { ClientAccountCreateForm } from "./accounts/ClientAccountCreateForm";

/** Which endpoint family owns the accounts being managed. */
export type ClientAccountsScope = "client" | "organization";

export interface ClientAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Client id when `scope` is "client", organization id when it is "organization". */
  entityId: string | null;
  entityDisplayName: string;
  /**
   * Declared by the caller — never inferred from the user type. Organization accounts live under
   * `/internal/organizations/:id/accounts` and client accounts under `/internal/clients/:id/accounts`;
   * inferring the scope from `isInternalUser` sent client ids to the organization endpoints (404).
   */
  scope: ClientAccountsScope;
}

export function ClientAccountsModal({
  isOpen,
  onClose,
  entityId,
  entityDisplayName,
  scope,
}: ClientAccountsModalProps) {
  const { user } = useAuth();
  const allowCreateAccount = canWriteClientAccounts(user);
  const isOrganizationScope = scope === "organization";
  const { t } = useTranslations("clients");

  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Id of the LATEST requested listing — drops out-of-order responses when the consumer swaps entities.
  const requestedEntityIdRef = useRef<string | null>(null);

  // Fed by the extracted create form so closing stays blocked while a create is in flight.
  const [submitting, setSubmitting] = useState(false);

  const [accountPendingDelete, setAccountPendingDelete] = useState<ClientAccount | null>(null);
  const [deleteStep, setDeleteStep] = useState<"first" | "final" | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadAccounts = useCallback(
    async (targetId: string) => {
      requestedEntityIdRef.current = targetId;
      setListLoading(true);
      setListError(null);
      setAccounts([]);

      try {
        const { data } = isOrganizationScope
          ? await ClientsService.fetchOrganizationAccounts(targetId)
          : await ClientsService.fetchAccounts(targetId);
        if (requestedEntityIdRef.current !== targetId) return;
        setAccounts(Array.isArray(data) ? data : []);
      } catch (err) {
        if (requestedEntityIdRef.current !== targetId) return;
        setListError(HttpApiService.getErrorMessage(err));
        setAccounts([]);
      } finally {
        if (requestedEntityIdRef.current === targetId) {
          setListLoading(false);
        }
      }
    },
    [isOrganizationScope],
  );

  useEffect(() => {
    if (!isOpen || !entityId) {
      requestedEntityIdRef.current = null;
      setAccounts([]);
      setListError(null);
      setListLoading(false);
      return;
    }
    void loadAccounts(entityId);
  }, [isOpen, entityId, loadAccounts]);

  const canDeleteCurrentAccount = isOrganizationScope
    ? canDeleteOrganizationAccounts(user)
    : canDeleteSubclientAccounts(user);

  /** Create endpoint chosen by scope — the form owns the field logic. */
  const handleCreateAccount = useCallback(
    async (body: CreateClientAccountRequest): Promise<ClientAccount> => {
      if (!entityId) throw new Error("missing entity");
      const { data } = isOrganizationScope
        ? await ClientsService.createOrganizationAccount(entityId, body)
        : await ClientsService.createAccount(entityId, body);
      return data;
    },
    [entityId, isOrganizationScope],
  );

  const handleAccountCreated = useCallback(() => {
    if (!entityId) return;
    void loadAccounts(entityId);
  }, [entityId, loadAccounts]);

  const handleClose = useCallback(() => {
    if (!submitting && !deleting) onClose();
  }, [onClose, submitting, deleting]);

  const handleRequestDelete = useCallback((account: ClientAccount) => {
    setAccountPendingDelete(account);
    setDeleteStep("first");
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (deleting) return;
    setAccountPendingDelete(null);
    setDeleteStep(null);
  }, [deleting]);

  const handleProceedToFinalConfirm = useCallback(() => {
    setDeleteStep("final");
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!accountPendingDelete || !entityId) return;
    setDeleting(true);
    try {
      if (isOrganizationScope) {
        await ClientsService.deleteOrganizationAccount(entityId, accountPendingDelete.id);
      } else {
        await ClientsService.deleteAccount(entityId, accountPendingDelete.id);
      }
      colocaToast.success(t("accounts_toast_delete_success"));
      setAccountPendingDelete(null);
      setDeleteStep(null);
      await loadAccounts(entityId);
    } catch (err) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("accounts_toast_delete_error"));
    } finally {
      setDeleting(false);
    }
  }, [accountPendingDelete, entityId, isOrganizationScope, loadAccounts, t]);

  const paymentMethodLabel = useCallback(
    (pm: ClientAccount["payment_method"]) => {
      if (pm === PublicAccountPaymentMethodEnum.CRYPTO_WALLET) {
        return t("accounts_payment_method_crypto");
      }
      if (pm === PublicAccountPaymentMethodEnum.BINANCE_PAY) {
        return t("accounts_payment_method_binance_pay");
      }
      if (pm === PublicAccountPaymentMethodEnum.BANK) {
        return t("accounts_payment_method_bank");
      }
      return pm ?? "—";
    },
    [t],
  );

  const title = t("accounts_modal_title", { name: entityDisplayName || "—" });

  return (
    <>
      <ColocaDrawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        size="xl"
        title={title}
      >
        <div className="space-y-8">
          <section>
            <h3 className="text-base font-semibold text-foreground mb-3">{t("accounts_section_list")}</h3>
            {listLoading && <p className="text-sm text-muted-foreground">{t("accounts_loading")}</p>}
            {listError && !listLoading && (
              <p className="text-sm text-destructive">{listError || t("accounts_error_load")}</p>
            )}
            {!listLoading && !listError && accounts.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("accounts_empty")}</p>
            )}
            {!listLoading && !listError && accounts.length > 0 && (
              <ul className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {accounts.map((acc) => (
                  <li
                    key={acc.id}
                    className={cn(
                      "rounded-lg border border-border p-3 text-sm",
                      acc.deleted_at && "opacity-60",
                    )}
                  >
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-foreground">
                      <span className="font-medium">{acc.currency ?? "—"}</span>
                      {(() => {
                        const currency = acc.currency?.trim();
                        const bancoAch = acc.account_metadata?.["bancoAch"] as unknown;
                        const shouldHideBankName = currency === "COP" && bancoAch != null;
                        if (shouldHideBankName) return null;
                        return <span>{acc.bank_name ?? "—"}</span>;
                      })()}
                      <span className="text-muted-foreground">{paymentMethodLabel(acc.payment_method)}</span>
                    </div>
                    {acc.account_metadata && Object.keys(acc.account_metadata).length > 0 && (
                      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {Object.entries(acc.account_metadata).map(([key, val]) => (
                          <div key={key} className="flex gap-1 min-w-0">
                            <dt className="font-medium shrink-0">{key}:</dt>
                            <dd className="truncate">
                              {val != null && typeof val === "object" ? JSON.stringify(val) : String(val)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-muted-foreground text-xs whitespace-nowrap">
                        {t("accounts_created_at")}: {formatDateTime(acc.created_at, { includeTime: true })}
                      </span>
                      {acc.deleted_at && (
                        <span className="text-xs text-warning whitespace-nowrap">
                          {t("accounts_deleted")}
                        </span>
                      )}
                      {canDeleteCurrentAccount && !acc.deleted_at && (
                        <button
                          type="button"
                          onClick={() => handleRequestDelete(acc)}
                          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-danger hover:text-danger/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={deleting}
                          aria-label={t("accounts_btn_delete")}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("accounts_btn_delete")}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {allowCreateAccount && (
            <section className="border-t border-border pt-6">
              <h3 className="text-base font-semibold text-foreground mb-4">{t("accounts_section_new")}</h3>
              {/* Remount when the target entity changes so the extracted form resets. */}
              <ClientAccountCreateForm
                key={entityId ?? "none"}
                disabled={!entityId}
                createAccount={handleCreateAccount}
                onCreated={handleAccountCreated}
                onSubmittingChange={setSubmitting}
              />
            </section>
          )}
        </div>
        {/* Sentinel: defeats ColocaDrawer's single-child sticky-footer behavior — this panel manages its own
            layout (list + create form), so the content must scroll normally. */}
        <span aria-hidden className="hidden" />
      </ColocaDrawer>

      <ColocaDialog
        open={deleteStep === "first"}
        onOpenChange={(open) => {
          if (!open) handleCancelDelete();
        }}
        title={t("accounts_delete_confirm_title")}
        size="default"
        footer={
          <>
            <ColocaButton type="button" variant="ghost" onClick={handleCancelDelete} disabled={deleting}>
              {t("accounts_delete_cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="destructive"
              onClick={handleProceedToFinalConfirm}
              disabled={deleting}
              icon={Trash2}
            >
              {t("accounts_delete_confirm")}
            </ColocaButton>
          </>
        }
      >
        <DeleteAccountModalBody
          account={accountPendingDelete}
          message={t("accounts_delete_confirm_message")}
          sectionHeading={t("accounts_delete_section_heading")}
          createdLabel={t("accounts_created_at")}
          noMetadataLabel={t("accounts_delete_no_metadata")}
          paymentMethodLabel={paymentMethodLabel(accountPendingDelete?.payment_method)}
          tone="warning"
        />
      </ColocaDialog>

      <ColocaDialog
        open={deleteStep === "final"}
        onOpenChange={(open) => {
          if (!open) handleCancelDelete();
        }}
        title={t("accounts_delete_final_confirm_title")}
        size="default"
        footer={
          <>
            <ColocaButton type="button" variant="ghost" onClick={handleCancelDelete} disabled={deleting}>
              {t("accounts_delete_cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
              loading={deleting}
              icon={Trash2}
            >
              {t("accounts_delete_final_confirm_button")}
            </ColocaButton>
          </>
        }
      >
        <DeleteAccountModalBody
          account={accountPendingDelete}
          message={t("accounts_delete_final_confirm_message")}
          sectionHeading={t("accounts_delete_section_heading")}
          createdLabel={t("accounts_created_at")}
          noMetadataLabel={t("accounts_delete_no_metadata")}
          paymentMethodLabel={paymentMethodLabel(accountPendingDelete?.payment_method)}
          tone="danger"
        />
      </ColocaDialog>
    </>
  );
}

interface DeleteAccountModalBodyProps {
  account: ClientAccount | null;
  message: string;
  sectionHeading: string;
  createdLabel: string;
  noMetadataLabel: string;
  paymentMethodLabel: string;
  tone: "warning" | "danger";
}

function DeleteAccountModalBody({
  account,
  message,
  sectionHeading,
  createdLabel,
  noMetadataLabel,
  paymentMethodLabel,
  tone,
}: DeleteAccountModalBodyProps) {
  if (!account) return null;

  const currency = account.currency?.trim() || "—";
  const bancoAch = account.account_metadata?.["bancoAch"] as unknown;
  const shouldHideBankName = currency === "COP" && bancoAch != null;
  const bankName = shouldHideBankName ? null : (account.bank_name ?? "—");
  const metadataEntries = account.account_metadata ? Object.entries(account.account_metadata) : [];

  const alertWrapperClass =
    tone === "danger"
      ? "flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-container p-3"
      : "flex items-start gap-3 rounded-xl border border-warning/30 bg-warning-container p-3";
  const alertIconClass = tone === "danger" ? "h-5 w-5 shrink-0 text-danger" : "h-5 w-5 shrink-0 text-warning";
  const alertTextClass =
    tone === "danger"
      ? "text-sm leading-relaxed text-danger-container-foreground"
      : "text-sm leading-relaxed text-warning-container-foreground";

  return (
    <div className="space-y-4">
      <div className={alertWrapperClass}>
        <AlertTriangle className={alertIconClass} />
        <p className={alertTextClass}>{message}</p>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {sectionHeading}
        </p>
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-muted px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-danger-container px-2 text-xs font-bold text-danger-container-foreground">
                {currency}
              </span>
              {bankName && <span className="truncate text-sm font-semibold text-foreground">{bankName}</span>}
            </div>
            <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {paymentMethodLabel}
            </span>
          </div>

          <div className="px-4 py-3">
            {metadataEntries.length > 0 ? (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
                {metadataEntries.map(([key, val]) => (
                  <div key={key} className="flex min-w-0 flex-col">
                    <dt className="font-medium uppercase tracking-wide text-muted-foreground">{key}</dt>
                    <dd className="truncate text-foreground">
                      {val != null && typeof val === "object" ? JSON.stringify(val) : String(val)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-xs italic text-muted-foreground">{noMetadataLabel}</p>
            )}
          </div>

          <div className="border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
            {createdLabel}: {formatDateTime(account.created_at, { includeTime: true })}
          </div>
        </div>
      </div>
    </div>
  );
}
